import { Box, FormControl, FormHelperText, Grid, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent, Stack, Typography } from '@mui/material'
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { useFormik } from 'formik';
import { FunctionComponent, SetStateAction, useContext, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { EmployeeCommuteFrequency } from '../../../../shared/interfaces/survey-frequency';
import moment, { Moment } from 'moment';
import { IPostEmailCampaign } from '../../../../modules/survey/shared-services/email-campaign/email-campaign.model';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import { IAllowedInputDates } from '../../../../context/report-periods/report-periods.model';
import { DialogSaveButtons } from '../../miscellaneous-components/dialog-save-buttons';
import { useIsReportPeriodLocked } from '../../../../hooks/use-is-report-period-locked';

export const EmissionsEmailCampaignScheduleForm:
  FunctionComponent<{
    newSurvey: IPostEmailCampaign,
    setNewSurvey: React.Dispatch<SetStateAction<any>>,
    handleNext?: () => void,
    submitSchedule?: (values?: Partial<IPostEmailCampaign>) => void,
    isSubmitted?: number;
    isEditPage?: boolean,
    isEditPageSubmitting?: boolean,
  }> = ({
    newSurvey,
    setNewSurvey,
    handleNext,
    submitSchedule,
    isSubmitted,
    isEditPage = false,
    isEditPageSubmitting
  }) => {
    //Services
    const reportPeriodContext = useContext(ReportPeriodContext);
    //Hooks
    const isReportPeriodLocked = useIsReportPeriodLocked()
    //State
    const [openDate, setOpenDate] = useState(false);
    const [openTime, setOpenTime] = useState(false);
    const [formRendered, setFormRendered] = useState<boolean>(false);
    const [reportPeriodDates, setReportPeriodDates] = useState<IAllowedInputDates>();

    useEffect(() => {
      if (formRendered && !isEditPage) {
        formik.submitForm();
      }
      setFormRendered(true)
    }, [isSubmitted])

    useEffect(() => {
      if (reportPeriodContext.lastSelectedReportId) {
        setReportPeriodDates(reportPeriodContext.getAllowedInputDates())
        const dates = reportPeriodContext.getAllowedInputDates()
        if (new Date(dates.endDate!).getTime() <= Date.now()) {
          formik.setFieldValue('emailFrequency', 'annual')
        }
      }
    }, [isSubmitted, reportPeriodContext.lastSelectedReportId])

    const employeeCommuteSchema = Yup.object().shape({
      name: Yup.string().required('Name of survey is required'),
      emailFrequency: Yup.string().required('Frequency of sending the email is required'),
      reminderAfterDays: Yup.number().min(0, 'Minumum is 0 day, which means no reminder').max(30, 'Maximum is 30 days').nullable(),
      sendDate: Yup.string().required('The date to send the survey is required'),
      sendTime: Yup.string().required('Time for email to be sent is required '),
    })
    const formik = useFormik({

      initialValues: {
        name: newSurvey.name,
        emailFrequency: newSurvey.emailFrequency,
        campaignType: newSurvey.campaignType,
        reminderAfterDays: newSurvey.reminderAfterDays,
        sendDate: newSurvey.sendDate,
        sendTime: newSurvey.sendTime,
        tokenExpiryDays: newSurvey.tokenExpiryDays,
        reportPeriodId: newSurvey.reportPeriodId,
      },
      validationSchema: employeeCommuteSchema,
      onSubmit: (values) => {
        setNewSurvey({
          ...newSurvey,
          name: values.name,
          emailFrequency: values.emailFrequency,
          reminderAfterDays: values.reminderAfterDays,
          sendDate: values.sendDate,
          sendTime: values.sendTime,
        })

        if (handleNext) {
          handleNext()
        }
        if (submitSchedule) {
          submitSchedule(values)
        }
      }
    });

    const dateHandler = (date: Moment | null) => {
      if (date) {
        formik.setValues({ ...formik.values, sendDate: date.format('YYYY-MM-DD') })
      }
    }

    const timeHandler = (time: Moment | null) => {
      if (time) {
        const formattedTime = time.format('HH:mm');
        formik.setValues({ ...formik.values, sendTime: formattedTime })
      }
    }

    const cancelHandler = () => {
      formik.resetForm();
    }

    return (
      <Box component='form' onSubmit={formik.submitForm}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel htmlFor='name'>Survey Name</InputLabel>
            <OutlinedInput inputProps={{ min: 0 }} id='name' label='Survey name' name='name'
              value={formik.values.name}
              onChange={formik.handleChange}
              disabled={isReportPeriodLocked === true}
              error={formik.touched.name && Boolean(formik.errors.name)}
              sx={{ color: 'text.primary' }}></OutlinedInput>
            {formik.touched.name && formik.errors.name &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.name}</FormHelperText>
            }
          </FormControl>
          <FormControl fullWidth variant='outlined' sx={{ mb: 2 }}>
            <InputLabel htmlFor='emailFrequency'>Frequency</InputLabel>
            <Select
              disabled={isEditPage || new Date(reportPeriodDates?.endDate!).getTime() <= Date.now() || isReportPeriodLocked === true}
              value={formik.values.emailFrequency}
              onChange={(e: SelectChangeEvent<string>) => {
                const value = e.target.value;
                formik.setFieldValue('emailFrequency', value);
              }}
              error={formik.touched.emailFrequency && Boolean(formik.errors.emailFrequency)}
              fullWidth
              id="emailFrequency"
              name="emailFrequency"
              label='frequency'
            >
              {new Date(reportPeriodDates?.endDate!).getTime() > Date.now() && Object.values(EmployeeCommuteFrequency).map(period => (
                <MenuItem key={period} value={period}>{period === 'annual' ? 'Once Only' : period.charAt(0).toUpperCase() + period.slice(1)}</MenuItem>
              ))}

              {new Date(reportPeriodDates?.endDate!).getTime() <= Date.now() && <MenuItem key={1} value={'annual'}>{'Once Only'}</MenuItem>}
            </Select>
            {formik.touched.emailFrequency && formik.errors.emailFrequency &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.emailFrequency}</FormHelperText>
            }
          </FormControl>
          <Grid container spacing={4}>
            <Grid item xs={6} md={6} lg={6}>
              <FormControl fullWidth variant='outlined' sx={{ mt: 1 }}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    value={formik.values.sendDate ? moment(formik.values.sendDate) : null}
                    format='DD/MM/YYYY'
                    label='Send Date'
                    minDate={moment()}
                    disabled={isEditPage || isReportPeriodLocked === true}
                    onChange={(date) => dateHandler(date)}
                    open={openDate}
                    onOpen={() => setOpenDate(true)}
                    onClose={() => setOpenDate(false)}
                    slotProps={{
                      textField: {
                        onClick: () => setOpenDate(true),
                        error: formik.touched.sendDate && Boolean(formik.errors.sendDate)
                      },
                    }}
                  />
                </LocalizationProvider>
                {formik.touched.sendDate && formik.errors.sendDate &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.sendDate}</FormHelperText>
                }
              </FormControl>
            </Grid>
            <Grid item xs={6} md={6} lg={6}>
              <FormControl fullWidth variant='outlined' sx={{ mt: 1 }}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <TimePicker
                    value={formik.values.sendTime ? moment(formik.values.sendTime, 'HH:mm') : null}
                    onChange={(time) => timeHandler(time)}
                    format='HH:mm'
                    disabled={isEditPage || isReportPeriodLocked === true}
                    label='Start Time'
                    open={openTime}
                    onOpen={() => setOpenTime(true)}
                    onClose={() => setOpenTime(false)}
                    slotProps={{
                      textField: {
                        onClick: () => setOpenTime(true),
                        error: formik.touched.sendTime && Boolean(formik.errors.sendTime)
                      },
                    }}
                  />
                </LocalizationProvider>
                {formik.touched.sendTime && formik.errors.sendTime &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.sendTime}</FormHelperText>
                }
              </FormControl>
            </Grid>
          </Grid>
          <FormControl fullWidth variant='outlined' sx={{ mt: 3 }}>
            <InputLabel htmlFor='reminderAfterDays'>Reminder (after number of days)</InputLabel>
            <OutlinedInput
              value={formik.values.reminderAfterDays}
              id="reminderAfterDays"
              disabled={isEditPage || isReportPeriodLocked === true}
              name="reminderAfterDays"
              label='reminder (after number of days)'
              onChange={formik.handleChange}
              type='number'
              inputProps={{ min: 0, step: 1, max: 30 }}
              error={formik.touched.reminderAfterDays && Boolean(formik.errors.reminderAfterDays)}
              fullWidth
            ></OutlinedInput>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>*No email reminders will be sent to generic team email addresses</Typography>
            {formik.touched.reminderAfterDays && formik.errors.reminderAfterDays &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.reminderAfterDays}</FormHelperText>
            }
          </FormControl>
        </Box>
        {
          isEditPage &&
          <Stack direction='row' spacing={2} alignItems='center' justifyContent='flex-end' sx={{ mt: 3 }}>
            <DialogSaveButtons
              isForm={true}
              isSubmitting={isEditPageSubmitting}
              cancelHandler={cancelHandler}
              submitHandler={formik.submitForm}
              disableSaveButton={isReportPeriodLocked === true}
              disableCancelButton={isReportPeriodLocked === true}
            />
          </Stack>
        }
      </Box>
    )
  }